import type { Board } from '../lib/types';
import { distance as fmtDistance, hours, longHours, money } from '../lib/format';

/**
 * The dispatch side panel.
 *
 * Actions at the top, the day's totals beneath, matching where the incumbent
 * puts them. Cost leads because that is the number the business is actually
 * managing; distance and drive time are how it got there.
 */
export function DispatchSidebar({
  board,
  optimizing,
  onAddOrder,
  onFindFit,
  onBuildRoutes,
  onDispatchRoutes,
}: {
  board: Board | null;
  optimizing: boolean;
  onAddOrder: () => void;
  /** Preview where one more stop would go, without changing anything. */
  onFindFit: () => void;
  onBuildRoutes: () => void;
  onDispatchRoutes: () => void;
}) {
  const stops = board?.routes.flatMap((r) => r.stops) ?? [];
  const used = board?.routes.filter((r) => r.stops.length > 0).length ?? 0;
  const distance = board?.routes.reduce((n, r) => n + r.plannedDistanceM, 0) ?? 0;
  const drive = board?.routes.reduce((n, r) => n + r.plannedDriveS, 0) ?? 0;

  /*
   * Working time is the whole shift, not driving plus service: an hour spent
   * waiting for a time window to open is an hour the operation pays for, and
   * the incumbent counts it.
   */
  const work = board?.routes.reduce((n, r) => n + r.plannedWorkS, 0) ?? 0;

  /*
   * Cost as the solver priced it, per route, summed here.
   *
   * This used to reprice the day in the browser at a flat 20 an hour and 1 a
   * kilometre, which meant the headline figure had nothing to do with the
   * rates an operator had entered or with the objective the plan was chosen
   * for. It is still a planning signal rather than an invoice -- a finance
   * system pricing differently will disagree -- but it is now the same
   * arithmetic on both sides of the wire.
   */
  const cost = board?.routes.reduce((total, route) => total + route.cost.total, 0) ?? 0;
  const currency = board?.territory.currency ?? 'USD';
  const unit = board?.territory.distanceUnit ?? 'mi';

  return (
    <aside className="dispatch-side">
      <div className="ds-actions">
        <button type="button" className="ds-action" onClick={onAddOrder}>
          <PlusIcon />
          Add Order
        </button>
        <button type="button" className="ds-action" onClick={onFindFit} disabled={!board}>
          <PinIcon />
          Find a Fit
        </button>
        <button
          type="button"
          className="ds-action"
          onClick={onBuildRoutes}
          disabled={optimizing || !board}
        >
          {optimizing ? <span className="spinner" /> : <BoltIcon />}
          {optimizing ? 'Building Routes' : 'Build Routes'}
        </button>
        <button type="button" className="ds-action" onClick={onDispatchRoutes} disabled={!board}>
          <SendIcon />
          Dispatch Routes
        </button>
      </div>

      <dl className="ds-stats">
        <Row label="Cost" value={money(cost, currency)} strong />
        <Row label="Orders" value={String(stops.length)} />
        <Row label="Unassigned" value={String(board?.unassigned.length ?? 0)} />
        <Row label="Vehicles" value={`${used} of ${board?.routes.filter((r) => r.available !== false || r.stops.length > 0).length ?? 0}`} />
        <Row label="Working Time" value={longHours(work)} />
        <Row label="Driving Time" value={hours(drive)} />
        <Row label="Distance" value={fmtDistance(distance, unit)} />
      </dl>
    </aside>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="ds-stat" data-strong={strong ? 'true' : undefined}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function PlusIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 3.5v9M3.5 8h9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M8 14.5s4.5-4.2 4.5-7.8A4.5 4.5 0 0 0 3.5 6.7c0 3.6 4.5 7.8 4.5 7.8z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path d="M8 4.8v3.6M6.2 6.6h3.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function BoltIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M9 1.5L3.5 9H7.5l-.5 5.5L12.5 7H8.5l.5-5.5z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M14 2L1.5 7l5 2 2 5L14 2z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}
